import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import plageBackground from "@/assets/img/plage-background.png";

export function TrialEndPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isContactLoading, setIsContactLoading] = useState(false);
  const { data: activeOrg } = authClient.useActiveOrganization();
  const { hasValidSubscription, isLoading: isLoadingStatus } = useSubscriptionStatus();

  // Vérifier si l'utilisateur a encore accès à l'application
  useEffect(() => {
    if (isLoadingStatus) return;

    // Si l'abonnement est valide, rediriger vers le dashboard
    if (hasValidSubscription) {
      toast.success("Abonnement actif détecté");
      navigate("/dashboard", { replace: true });
    }
  }, [hasValidSubscription, isLoadingStatus, navigate]);

  const handleSubscribeToAnnual = async () => {
    setIsLoading(true);
    try {
      if (!activeOrg) {
        toast.error("Aucune organisation active trouvée");
        return;
      }

      console.log("Attempting subscription upgrade with:", {
        plan: "standard",
        referenceId: activeOrg.id,
      });

      await authClient.subscription.upgrade(
        {
          plan: "standard",
          referenceId: activeOrg.id,
          successUrl: window.location.origin + "/subscriptions/confirm",
          cancelUrl: window.location.origin + "/subscriptions/trial-end?error=cancelled",
        },
        {
          onError: (error) => {
            console.error("Detailed error:", error);
            toast.error("Erreur lors de l'activation de l'abonnement", {
              description: error.error?.message || "Une erreur inconnue s'est produite",
            });
          },
        }
      );
    } catch (error) {
      console.error("Catch block error:", error);
      toast.error("Erreur inattendue", {
        description: "Une erreur s'est produite lors de la création du paiement",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSupport = async () => {
    setIsContactLoading(true);
    try {
      const subject = encodeURIComponent("Support - Période d'essai expirée");
      const body = encodeURIComponent(
        `Bonjour,

Ma période d'essai est expirée et j'aimerais obtenir de l'aide concernant mon abonnement.

`
      );

      const mailtoUrl = `mailto:support@nautika.app?subject=${subject}&body=${body}`;
      window.location.href = mailtoUrl;
    } catch (error) {
      console.error("Error opening email client:", error);
      toast.error("Erreur lors de l'ouverture du client email");
    } finally {
      setIsContactLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await authClient.signOut();
      toast.success("Déconnexion réussie");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Erreur lors de la déconnexion");
    }
  };

  // Afficher un loader si on est en train de vérifier le statut
  if (isLoadingStatus) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center"
      style={{
        backgroundImage: `url(${plageBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Overlay pour améliorer la lisibilité */}
      <div className="absolute inset-0 bg-black/20" />

      <div className="relative z-10 mx-4 w-full max-w-md">
        <div className="rounded-lg bg-white p-8 text-center shadow-xl">
          {/* Icône parasol */}
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
          </div>

          {/* Titre */}
          <h1 className="mb-3 text-2xl font-bold text-gray-900">Votre période d'essai est terminée</h1>

          {/* Description */}
          <p className="mb-8 text-gray-600">Pour continuer à gérer votre club de plage, veuillez souscrire à un abonnement annuel.</p>

          {/* Prix */}
          <div className="mb-6 rounded-lg bg-blue-50 p-4">
            <div className="mb-1 text-3xl font-bold text-blue-600">480€ / an</div>
            <div className="text-sm text-blue-600">Accès complet pour toute la saison</div>
          </div>

          {/* Bouton principal */}
          <Button onClick={handleSubscribeToAnnual} className="mb-4 w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirection en cours...
              </>
            ) : (
              "Continuer à gérer mon club"
            )}
          </Button>

          {/* Message de sauvegarde */}
          <p className="mb-4 text-sm text-gray-500">Vos données sont sauvegardées et vous attendent.</p>

          {/* Boutons de contact et déconnexion */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleContactSupport} className="flex-1" disabled={isContactLoading}>
              {isContactLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ouverture...
                </>
              ) : (
                "Nous contacter"
              )}
            </Button>
            <Button variant="destructive" onClick={handleSignOut} className="flex-1">
              Se déconnecter
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
